import { useQuery, useQueries } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { DisplayInput } from "@/components/Atoms/DisplayField";
import {
	DisplaySelect,
	DisplaySelectBadgeVariant,
	DisplaySelectValueType,
} from "@/components/Atoms/DisplayField/DisplaySelect";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator/use-field-feedback-map";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { fieldLessThanLength } from "@/components/form/validation/create-common-validators";
import { usePlatformUserEditAccess } from "@/features/users/platform-user-edit-access";
import { PlatformUserStatusActionModal } from "@/features/users/platform-user-status-action-modal";
import {
	PLATFORM_USER_STATUS_ACTIONS,
	platformUserStatusActionName,
	platformUserStatusActionsFromPermissions,
	platformUserStatusApiValue,
	type PlatformUserStatusAction,
} from "@/features/users/platform-user-status-actions";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth";
import { userQueryOptions } from "@/services/user";
import {
	useUpdateUserInfo,
	useUpdateUserPlatformRole,
	useUpdateUserStatus,
} from "@/services/user/user.mutations";
import { formatId } from "@/utils/format-id";
import { showApiErrorToast, showSuccessToast } from "@/utils/toast.utils";
import { SearchSchema } from "../$userId";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users/(platform-users)/platform/$userId/details",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

type NameField = "firstName" | "lastName";
type FeedbackField = NameField | "role";

const STATUS_BADGE_COLORS = {
	Active: "success",
	Suspended: "warning",
	Archived: "gray",
	Retired: "error",
} as const;

function RouteComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/users/(platform-users)/platform/$userId/details",
	});
	const { data: platformRoles = [] } = useQuery(
		userQueryOptions.platformRoles(),
	);
	const userById = useQuery(userQueryOptions.detail(params.userId));
	const { access: editAccess, isPending: editAccessPending } =
		usePlatformUserEditAccess(params.userId);
	const canEditName = editAccess.canEditProfile && !editAccessPending;
	const canEditRole = editAccess.canEditRoleStatus && !editAccessPending;

	const updateUser = useUpdateUserInfo();
	const updateRole = useUpdateUserPlatformRole();
	const updateStatus = useUpdateUserStatus();
	const { feedbackByField, setFieldFeedback } =
		useFieldFeedbackMap<FeedbackField>();

	const [editingField, setEditingField] = useState<NameField | null>(null);
	const [draftByField, setDraftByField] = useState<
		Partial<Record<NameField, string>>
	>({});
	const [errorByField, setErrorByField] = useState<
		Partial<Record<NameField, string>>
	>({});
	const [pendingStatusAction, setPendingStatusAction] =
		useState<PlatformUserStatusAction | null>(null);
	const [retireEmail, setRetireEmail] = useState("");

	const statusActionChecks = useQueries({
		queries: PLATFORM_USER_STATUS_ACTIONS.map((action) =>
			authQueryOptions.checkAction({
				name: platformUserStatusActionName(action),
				entityType: "User",
				entityId: params.userId,
			}),
		),
	});
	const statusActions = platformUserStatusActionsFromPermissions(
		Object.fromEntries(
			PLATFORM_USER_STATUS_ACTIONS.map((action, index) => [
				action,
				statusActionChecks[index]?.data === true,
			]),
		),
	);

	const user = userById.data;
	const memberName =
		[user?.firstName, user?.lastName]
			.filter((part) => part?.trim())
			.join(" ")
			.trim() ||
		user?.displayName ||
		"";
	const statusName = user?.status?.name ?? "";
	const statusLabel =
		(statusName &&
			t(
				`admin.users.drawer.platform.statuses.${statusName.toLowerCase()}`,
			)) ||
		statusName;
	const statusBadgeColor =
		STATUS_BADGE_COLORS[statusName as keyof typeof STATUS_BADGE_COLORS] ??
		"gray";
	const currentRole = user?.platformRoles?.[0];

	const serverName = (field: NameField) => user?.[field] ?? "";

	const setNameError = (field: NameField, message: string | undefined) => {
		setErrorByField((prev) => ({ ...prev, [field]: message }));
	};

	const clearNameDraft = (field: NameField) => {
		setDraftByField((prev) => {
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const beginNameEdit = (field: NameField) => {
		setNameError(field, undefined);
		setEditingField(field);
		setDraftByField((prev) => ({ ...prev, [field]: serverName(field) }));
	};

	const cancelNameEdit = (field: NameField) => {
		setNameError(field, undefined);
		setFieldFeedback(field, DisplayFieldFeedback.Idle);
		clearNameDraft(field);
		setEditingField((current) => (current === field ? null : current));
	};

	const commitName = async (field: NameField) => {
		const raw = draftByField[field];
		if (raw === undefined) {
			setEditingField((current) => (current === field ? null : current));
			return;
		}
		const next = raw.trim();
		if (next === serverName(field)) {
			cancelNameEdit(field);
			return;
		}
		const fieldLabel = t(
			field === "firstName" ? "User.FirstName" : "User.LastName",
			{ useFieldInfo: true },
		);
		if (!next) {
			setNameError(
				field,
				t(
					field === "firstName"
						? "common.validation.firstNameRequired"
						: "common.validation.lastNameRequired",
				),
			);
			return;
		}
		if (next.length > 50) {
			setNameError(field, fieldLessThanLength(t, fieldLabel, 50));
			return;
		}
		setEditingField((current) => (current === field ? null : current));
		setFieldFeedback(field, DisplayFieldFeedback.Saving);
		try {
			await updateUser.mutateAsync({
				userId: params.userId,
				data: {
					[field]: next,
					displayName: user?.displayName ?? null,
				},
			});
			setNameError(field, undefined);
			clearNameDraft(field);
			setFieldFeedback(field, DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback(field, DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const saveRole = async (roleCode: string) => {
		const systemRoleId = Number(roleCode);
		if (!Number.isFinite(systemRoleId) || systemRoleId === currentRole?.code) {
			return;
		}
		setFieldFeedback("role", DisplayFieldFeedback.Saving);
		try {
			await updateRole.mutateAsync({
				userId: params.userId,
				data: { systemRoleId },
			});
			setFieldFeedback("role", DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback("role", DisplayFieldFeedback.Idle);
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const closeStatusAction = () => {
		if (updateStatus.isPending) return;
		setPendingStatusAction(null);
		setRetireEmail("");
	};

	const confirmStatusAction = async () => {
		if (!pendingStatusAction) return;
		const action = pendingStatusAction;
		try {
			await updateStatus.mutateAsync({
				userId: params.userId,
				data: { status: platformUserStatusApiValue(action) },
			});
			showSuccessToast({
				message: t(
					`admin.users.drawer.platform.statusActions.toast.${
						action === "activate"
							? "activated"
							: action === "suspend"
								? "suspended"
								: action === "archive"
									? "archived"
									: "retired"
					}`,
					{ name: memberName },
				),
			});
			setPendingStatusAction(null);
			setRetireEmail("");
			if (action === "archive" || action === "retire") {
				navigate({
					to: "/admin/users/platform",
					replace: true,
				});
			}
		} catch (error) {
			showApiErrorToast({
				message: t("common.errors.unknownError"),
				error,
			});
		}
	};

	const nameField = (field: NameField, label: string) => {
		const isEditing = editingField === field;
		const draft = draftByField[field] ?? serverName(field);
		const error = errorByField[field];
		return (
			<DisplayInput
				label={label}
				data-testid={
					field === "firstName"
						? "platform-user-details-first-name"
						: undefined
				}
				value={isEditing ? draft : serverName(field) || undefined}
				state={
					isEditing
						? DisplayFieldState.Active
						: error
							? DisplayFieldState.Error
							: DisplayFieldState.Enabled
				}
				feedback={feedbackByField[field] ?? DisplayFieldFeedback.Idle}
				hintText={error}
				showHintText={Boolean(error)}
				showEdit={canEditName}
				editValue={isEditing ? draft : undefined}
				onEdit={canEditName ? () => beginNameEdit(field) : undefined}
				onEditChange={(value) => {
					setDraftByField((prev) => ({ ...prev, [field]: value }));
					setNameError(field, undefined);
				}}
				onEditCommit={() => void commitName(field)}
				onEditCancel={() => cancelNameEdit(field)}
			/>
		);
	};

	return (
		<div className="flex w-full flex-col">
			{nameField(
				"firstName",
				t("User.FirstName", { useFieldInfo: true }),
			)}
			{nameField("lastName", t("User.LastName", { useFieldInfo: true }))}
			<DisplayInput
				label={t("User.Email", { useFieldInfo: true })}
				value={user?.email}
				showEdit={false}
			/>
			<DisplaySelect
				label={t("admin.users.drawer.platform.details.role")}
				data-testid="platform-user-role-field"
				value={currentRole?.name}
				valueType={DisplaySelectValueType.Text}
				showValueIcon={false}
				showEdit={canEditRole}
				options={
					canEditRole
						? platformRoles.map((role) => ({
								id: String(role.code),
								label: role.name,
							}))
						: undefined
				}
				selectedKey={
					currentRole ? String(currentRole.code) : undefined
				}
				feedback={feedbackByField.role ?? DisplayFieldFeedback.Idle}
				onSelectionChange={(key) => void saveRole(key)}
			/>
			<DisplaySelect
				label={t("admin.users.drawer.platform.details.platformStatus")}
				data-testid="platform-user-status-field"
				value={statusLabel || undefined}
				valueType={DisplaySelectValueType.Badge}
				badgeVariant={DisplaySelectBadgeVariant.Dot}
				showValueIcon={false}
				badgeColor={statusBadgeColor}
				showEdit={statusActions.length > 0}
				options={
					statusActions.length > 0
						? statusActions.map((action) => ({
								id: action,
								label: t(
									`admin.users.drawer.platform.statusActions.${action}.action`,
								),
								testId: `platform-user-status-action-${action}`,
							}))
						: undefined
				}
				onSelectionChange={(key) => {
					setRetireEmail("");
					setPendingStatusAction(key as PlatformUserStatusAction);
				}}
			/>
			<DisplayInput
				label={t("admin.users.drawer.platform.details.userId")}
				value={formatId(user?.id) || undefined}
				copyable
				copyText={user?.id}
				showEdit={false}
			/>

			<PlatformUserStatusActionModal
				isOpen={pendingStatusAction != null}
				variant={pendingStatusAction ?? "suspend"}
				email={retireEmail}
				userEmail={user?.email ?? ""}
				userName={memberName}
				isLoading={updateStatus.isPending}
				onEmailChange={setRetireEmail}
				onConfirm={confirmStatusAction}
				onCancel={closeStatusAction}
			/>
		</div>
	);
}
